import type { IntlShape } from "@formatjs/intl";
import { Badge, Card, Flex, Typography } from "antd";
import type { StatusCount } from "../../generated-axios-client";
import { palette } from "../../theme";
import { statusLabelMap } from "../../ticket/ticketConstants";
import { WO_STATUS_COLORS } from "../dashboard.constants";
import styles from "../Dashboard.module.css";

type Props = {
  intl: IntlShape;
  counts: StatusCount[] | undefined;
  total: number;
  loading: boolean;
  onOpenStatus: (status: string) => void;
};

const RADIUS = 54;
const STROKE = 18;
const SIZE = 148;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export const WorkOrdersByStatusChart = ({
  intl,
  counts,
  total,
  loading,
  onOpenStatus,
}: Props) => {
  let cumulativeDash = 0;
  const slices = (counts ?? []).map(({ status, count }) => {
    const dash = total > 0 ? (count / total) * CIRCUMFERENCE : 0;
    const offset = cumulativeDash;
    cumulativeDash += dash;
    return { status, count, dash, gap: CIRCUMFERENCE - dash, offset };
  });

  return (
    <Card
      className={styles.fullHeight}
      title={intl.formatMessage({ id: "dashboard_status_title" })}
      loading={loading}
    >
      <Flex gap="large" align="center" wrap>
        <svg
          className={styles.donut}
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label={intl.formatMessage({ id: "dashboard_status_title" })}
        >
          <g transform={`rotate(-90, ${SIZE / 2}, ${SIZE / 2})`}>
            {total === 0 ? (
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={palette.cardBorder}
                strokeWidth={STROKE}
              />
            ) : (
              slices.map(({ status, dash, gap, offset }) => (
                <circle
                  key={status}
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  stroke={WO_STATUS_COLORS[status] ?? palette.cardBorder}
                  strokeWidth={STROKE}
                  strokeDasharray={`${dash} ${gap}`}
                  strokeDashoffset={-offset}
                />
              ))
            )}
          </g>
          <text
            className={styles.donutTotal}
            x={SIZE / 2}
            y={SIZE / 2 - 4}
            textAnchor="middle"
          >
            {total}
          </text>
          <text
            className={styles.donutLabel}
            x={SIZE / 2}
            y={SIZE / 2 + 16}
            textAnchor="middle"
          >
            {intl.formatMessage({ id: "dashboard_open_total" })}
          </text>
        </svg>

        <Flex vertical gap="small" className={styles.legend}>
          {slices.map(({ status, count }) => (
            <Flex key={status} justify="space-between" align="center" gap="small">
              <Badge
                color={WO_STATUS_COLORS[status]}
                text={
                  <Typography.Link onClick={() => onOpenStatus(status)}>
                    {statusLabelMap[status] ?? status}
                  </Typography.Link>
                }
              />
              <Typography.Text strong>{count}</Typography.Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Card>
  );
};
